import { ConsentManagementConfigVersion } from "@/api";
import { applyTransportDerivations } from "@/consent-management/utils/freshpaint";
import { deepDiffObjects, FieldDiffDesc } from "@/utils/object";
import { ConditionalConfig, ConsentManagerConfigSchema } from "./schema";

/**
 * Translate live form values into the SDK-ready preview shape. Routes
 * through `applyTransportDerivations` so the preview's interpretation of
 * `categorySettings` stays in lockstep with what `onSubmit` saves.
 */
export function draftValuesToPreviewConfig(
  draftValues: ConsentManagerConfigSchema | ConsentManagementConfigVersion,
): ConsentManagementConfigVersion {
  const freshpaintConfig = draftValues.freshpaintConfig
    ? {
        ...applyTransportDerivations(
          draftValues.freshpaintConfig,
          draftValues.categoryMapping,
          draftValues.consentModel,
        ),
        mode: draftValues.consentModel,
      }
    : undefined;

  return {
    ...draftValues,
    ...(freshpaintConfig && { freshpaintConfig }),
  } as ConsentManagementConfigVersion;
}

/**
 * Resolve the privacy policy fields a targeted (conditional) config should
 * render with. Mirrors the SDK's runtime fallback: an empty value on the
 * conditional inherits the global default. Keeps existing configs (created
 * before these fields were per-conditional) linking to the global policy.
 */
export function resolveInheritedPrivacyPolicy(
  global: NonNullable<ConsentManagerConfigSchema["freshpaintConfig"]>,
  conditional: Partial<
    Pick<ConditionalConfig, "privacyPolicyUrl" | "privacyPolicyLinkText">
  >,
): { privacyPolicyUrl: string; privacyPolicyLinkText: string } {
  return {
    privacyPolicyUrl:
      conditional.privacyPolicyUrl?.trim() || global.privacyPolicyUrl || "",
    privacyPolicyLinkText:
      conditional.privacyPolicyLinkText?.trim() ||
      global.privacyPolicyLinkText ||
      "",
  };
}

export type ConditionalConfigGroup = {
  label: string;
  action: "added" | "removed" | "modified";
  diffs?: FieldDiffDesc[];
};

export type ConsentFieldDiff = { type: "field"; diff: FieldDiffDesc };

export type ConsentConditionalConfigDiff = {
  type: "conditionalConfigs";
  groups: ConditionalConfigGroup[];
};

export type ConsentConfigDiff = ConsentFieldDiff | ConsentConditionalConfigDiff;

export function getConsentConfigDiffKey(item: ConsentConfigDiff): string {
  if (item.type === "conditionalConfigs") return "conditionalConfigs";
  return item.diff.path.join(".");
}

export function countConsentConfigDiffs(diffs: ConsentConfigDiff[]): number {
  return diffs.reduce((count, item) => {
    if (item.type === "field") return count + 1;
    // Count each group (add/remove/modified) as 1
    return count + item.groups.length;
  }, 0);
}

export function diffConsentConfigs({
  config1 = {} as ConsentManagementConfigVersion,
  config2,
}: {
  config1?: ConsentManagementConfigVersion;
  config2: ConsentManagementConfigVersion;
}): ConsentConfigDiff[] {
  const isFreshpaintConfig =
    config1.type === "freshpaint" || config2.type === "freshpaint";

  const excludePaths = [
    "id",
    "revision",
    "createdAt",
    "publishedAt",
    "publishedBy",
    "publishDescription",
    "updatedAt",
    "envID",
    "type",
    ...(isFreshpaintConfig
      ? [
          "freshpaintConfig.categories",
          "freshpaintConfig.language.translations.en.consentModal.footer",
          "freshpaintConfig.conditionalConfigs", // Handle separately
        ]
      : ["freshpaintConfig"]),
  ];

  // Get regular field diffs (excluding conditionalConfigs)
  const fieldDiffs = deepDiffObjects({
    obj1: config1,
    obj2: config2,
    excludePaths,
  });

  const result: ConsentConfigDiff[] = fieldDiffs.map((diff) => ({
    type: "field",
    diff,
  }));

  // Handle conditional configs separately
  if (isFreshpaintConfig) {
    const configs1 = (config1.freshpaintConfig?.conditionalConfigs ??
      []) as ConditionalConfig[];
    const configs2 = (config2.freshpaintConfig?.conditionalConfigs ??
      []) as ConditionalConfig[];

    const groups = diffConditionalConfigs(configs1, configs2);
    if (groups.length) {
      result.push({ type: "conditionalConfigs", groups });
    }
  }

  return result;
}

function diffConditionalConfigs(
  configs1: ConditionalConfig[],
  configs2: ConditionalConfig[],
): ConditionalConfigGroup[] {
  const groups: ConditionalConfigGroup[] = [];

  const byId1 = new Map(configs1.map((c) => [c.id, c]));
  const byId2 = new Map(configs2.map((c) => [c.id, c]));

  // Added configs
  for (const config of configs2) {
    if (!byId1.has(config.id)) {
      groups.push({ label: config.label, action: "added" });
    }
  }

  // Removed configs
  for (const config of configs1) {
    if (!byId2.has(config.id)) {
      groups.push({ label: config.label, action: "removed" });
    }
  }

  // Modified configs
  for (const config2 of configs2) {
    const config1 = byId1.get(config2.id);
    if (!config1) continue;

    const diffs = deepDiffObjects({
      obj1: config1,
      obj2: config2,
      excludePaths: [
        "id", // Don't show id changes
        "categories", // Derived from categorySettings; not user-editable
      ],
    });

    if (diffs.length) {
      groups.push({ label: config2.label, action: "modified", diffs });
    }
  }

  return groups;
}
